import {
  formatDerivedPeriod,
  formatDurationMonths,
  formatQuantityWithUnit,
  formatToTRY,
} from '../../utils';

export type PriceSummaryRow = {
  label: string;
  amount: number;
  isDiscount?: boolean;
  isGrand?: boolean;
};

export type EquipmentUngroupedRow = {
  key: string;
  name: string;
  maxDurationText: string;
  controlCount: number;
  periodText: string;
  quantityWithUnit: string;
  unitPrice: number;
  totalPrice: number;
};

export type EquipmentGroupedRow = {
  key: string;
  toolName: string;
  maxDurationText: string;
  controlCount: number;
  periodText: string;
  measurementDetails: string;
  totalPrice: number;
};

export type MeasurementRow = {
  key: string;
  name: string;
  maxDurationText: string;
  controlCount: number;
  periodText: string;
  quantityWithUnit: string;
  unitPrice: number;
  totalPrice: number;
};

export type MeasurementCompositeRow = {
  key: string;
  name: string;
  maxDurationText: string;
  controlCount: number;
  periodText: string;
  detail: string;
  totalPrice: number;
};

export type ProposalPriceTables = {
  equipmentUngrouped: EquipmentUngroupedRow[];
  equipmentGrouped: EquipmentGroupedRow[];
  measurementUngrouped: MeasurementRow[];
  measurementComposite: MeasurementCompositeRow[];
};

export type ProposalPricing = {
  groupedByOffering: any;
  tables: ProposalPriceTables;
  equipmentSummaryRows: PriceSummaryRow[];
  measurementSummaryRows: PriceSummaryRow[];
  formulaRows: Array<[string, string]>;
  subtotalExcludingKdv: number;
  kdvRate: number;
  kdvAmount: number;
  totalIncludingKdv: number;
};

const EQUIPMENT_OFFERINGS = new Set(['Elektrik', 'Makine']);
const DEFAULT_KDV_RATE = 20;

export const numberValue = (value: any): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizePeriod = (period: any): number => {
  const p = Number(period);
  if (!Number.isFinite(p) || p <= 0) {
    return 1;
  }
  return p;
};

const round2 = (value: number): number => Number((value || 0).toFixed(2));

const emptyTables = (): ProposalPriceTables => ({
  equipmentUngrouped: [],
  equipmentGrouped: [],
  measurementUngrouped: [],
  measurementComposite: [],
});

export const calculateEquipmentDisplayTotal = (offeringList: any[]): number => {
  let total = 0;
  const visitedToolIds = new Set<number>();

  for (const item of offeringList || []) {
    const attributes = Array.isArray(item?.attributes) ? item.attributes : [];
    for (const attribute of attributes) {
      if (!attribute?.toolId) {
        total += numberValue(attribute?.finalPrice);
        continue;
      }

      const toolId = numberValue(attribute.toolId);
      if (!visitedToolIds.has(toolId)) {
        total += numberValue(attribute?.groupTotalPrice);
        visitedToolIds.add(toolId);
      }
    }
  }

  return total;
};

const resolveKdvRate = (proposal: any): number => {
  return [
    proposal?.priceCalculationResponse?.kdvRate,
    proposal?.priceCalculationResponse?.vatRate,
    proposal?.kdvRate,
    proposal?.vatRate,
    proposal?.taxRate,
  ].map(numberValue).find((value) => value > 0) ?? DEFAULT_KDV_RATE;
};

const resolveSubtotal = (proposal: any, computedNetTotal: number): number => {
  const proposalTotal = numberValue(proposal?.totalPrice);
  if (proposalTotal > 0) {
    return proposalTotal;
  }

  const responseTotal = numberValue(proposal?.priceCalculationResponse?.totalPrice);
  if (responseTotal > 0) {
    return responseTotal;
  }

  return round2(computedNetTotal);
};

const buildFallbackPricing = (proposal: any): ProposalPricing => {
  const fallbackSubtotal =
    numberValue(proposal?.totalPrice) ||
    numberValue(proposal?.priceCalculationResponse?.totalPrice) ||
    numberValue(proposal?.priceCalculationResponse?.finalPrice);
  const kdvRate = resolveKdvRate(proposal);
  const kdvAmount = round2(fallbackSubtotal * (kdvRate / 100));

  return {
    groupedByOffering: {
      equipment: [],
      measurement: [],
      discount: numberValue(proposal?.discountedPrice),
      equipmentDiscountPart: 0,
      measurementDiscountPart: 0,
      equipmentNet: 0,
      measurementNet: 0,
      serviceFeeTotal: 0,
    },
    tables: emptyTables(),
    equipmentSummaryRows: [],
    measurementSummaryRows: [],
    formulaRows: [],
    subtotalExcludingKdv: fallbackSubtotal,
    kdvRate,
    kdvAmount,
    totalIncludingKdv: round2(fallbackSubtotal + kdvAmount),
  };
};

export const buildProposalPricing = (proposal: any, allAttributes: any[] = []): ProposalPricing => {
  const offeringLogs: any[] = proposal?.priceCalculationResponse?.offeringLogs ?? [];
  if (!Array.isArray(offeringLogs) || offeringLogs.length === 0) {
    return buildFallbackPricing(proposal);
  }

  const groupedByOffering: any = {
    equipment: [] as any[],
    measurement: [] as any[],
    discount: numberValue(proposal?.discountedPrice),
    serviceFeeTotal: 0,
  };
  const attributeMetaById = new Map<number, any>(
    allAttributes.map((attribute: any) => [numberValue(attribute?.id), attribute]),
  );

  const equipmentOffering: any = {
    offeringName: 'Elektrik ve Makine',
    isComposite: false,
    totalPrice: 0,
    finalPrice: 0,
    groupTotalPrice: 0,
    originalTotalPrice: 0,
    discountAmount: 0,
    serviceFeeTotal: 0,
    serviceFeeDifference: 0,
    electricServiceFeeDifference: 0,
    machineryServiceFeeDifference: 0,
    attributes: [] as any[],
  };

  offeringLogs.forEach((offering: any) => {
    const attributes: any[] = Array.isArray(offering?.attributes) ? offering.attributes : [];
    const isComposite: boolean = Boolean(offering?.composite);
    const offeringName = String(offering?.offeringName || '');
    const serviceFeeDifference = numberValue(offering?.serviceFeeDifference);

    let originalTotalPrice = 0;
    let discountedTotalPrice = 0;

    if (EQUIPMENT_OFFERINGS.has(offeringName)) {
      attributes.forEach((attr: any) => {
        const quantity = numberValue(attr?.quantity);
        const price = numberValue(attr?.price);
        const period = normalizePeriod(attr?.period);
        const discountDetails = Array.isArray(attr?.discountDetails) ? attr.discountDetails : [];
        const discountedTotal = discountDetails.length > 0
          ? discountDetails.reduce((sum: number, discount: any) => sum + (numberValue(discount?.price) * numberValue(discount?.quantity)), 0)
          : quantity * price;
        const matchedAttribute = attributeMetaById.get(numberValue(attr?.attributeId));

        equipmentOffering.attributes.push({
          name: attr?.attributeName || '-',
          quantity,
          price,
          finalPrice: numberValue(attr?.finalPrice),
          groupTotalPrice: numberValue(attr?.groupTotalPrice),
          period,
          toolId: attr?.toolId,
          toolName: attr?.toolName,
          attributeId: attr?.attributeId,
          originalPrice: quantity * price,
          discountedTotal,
          discountAmount: (quantity * price) - discountedTotal,
          discountDetails,
          order: numberValue(matchedAttribute?.order),
          unit: matchedAttribute?.unit,
          maxDurationMonths: numberValue(matchedAttribute?.period),
        });

        originalTotalPrice += quantity * price;
        discountedTotalPrice += discountedTotal;
      });

      equipmentOffering.attributes.sort((a: any, b: any) => numberValue(a?.order) - numberValue(b?.order));
      equipmentOffering.originalTotalPrice += originalTotalPrice;
      equipmentOffering.totalPrice += discountedTotalPrice;
      equipmentOffering.finalPrice += numberValue(offering?.finalPrice);
      equipmentOffering.discountAmount += originalTotalPrice - discountedTotalPrice;
      equipmentOffering.serviceFeeDifference += serviceFeeDifference;

      if (offeringName === 'Elektrik') {
        equipmentOffering.electricServiceFeeDifference += serviceFeeDifference;
      } else if (offeringName === 'Makine') {
        equipmentOffering.machineryServiceFeeDifference += serviceFeeDifference;
      }
      return;
    }

    const groupData: any = {
      offeringName,
      isComposite,
      totalPrice: numberValue(offering?.totalPrice),
      finalPrice: 0,
      groupTotalPrice: 0,
      originalTotalPrice: 0,
      discountAmount: 0,
      serviceFeeDifference,
      attributes: [] as any[],
    };

    attributes.forEach((attr: any) => {
      const quantity = numberValue(attr?.quantity);
      const price = numberValue(attr?.price);
      const period = normalizePeriod(attr?.period);
      const discountDetails = Array.isArray(attr?.discountDetails) ? attr.discountDetails : [];
      const discountedTotal = discountDetails.length > 0
        ? discountDetails.reduce((sum: number, discount: any) => sum + (numberValue(discount?.price) * numberValue(discount?.quantity)), 0)
        : quantity * price;
      const matchedAttribute = attributeMetaById.get(numberValue(attr?.attributeId));

      groupData.attributes.push({
        name: attr?.attributeName || '-',
        quantity,
        price,
        totalPrice: numberValue(attr?.finalPrice),
        finalPrice: numberValue(attr?.finalPrice),
        attributeId: attr?.attributeId,
        toolId: attr?.toolId,
        toolName: attr?.toolName,
        period,
        unit: matchedAttribute?.unit,
        maxDurationMonths: numberValue(matchedAttribute?.period),
        originalPrice: quantity * price,
        discountedTotal,
        discountAmount: (quantity * price) - discountedTotal,
        discountDetails,
      });

      groupData.originalTotalPrice += quantity * price;
      groupData.totalPrice += discountedTotal;
      groupData.finalPrice = numberValue(offering?.finalPrice);
      groupData.discountAmount += (quantity * price) - discountedTotal;
    });

    groupedByOffering.measurement.push(groupData);
  });

  if (equipmentOffering.attributes.length > 0) {
    groupedByOffering.equipment.push(equipmentOffering);
  }

  const allEquipmentAttributes: any[] = [];
  groupedByOffering.equipment.forEach((group: any) => {
    (group?.attributes || []).forEach((attr: any) => allEquipmentAttributes.push(attr));
  });

  const toolIdGrouped = allEquipmentAttributes.reduce((acc: any, attr: any) => {
    if (attr?.toolId) {
      const key = String(attr.toolId);
      if (!acc[key]) {
        acc[key] = { toolName: attr?.toolName, attributes: [] };
      }
      acc[key].attributes.push(attr);
    } else {
      acc.noToolId = acc.noToolId || [];
      acc.noToolId.push(attr);
    }
    return acc;
  }, {});

  const equipmentUngrouped: EquipmentUngroupedRow[] = (toolIdGrouped.noToolId || []).map((attr: any, index: number) => {
    const period = normalizePeriod(attr?.period);
    const maxDurationMonths = numberValue(attr?.maxDurationMonths);
    const unitPrice = numberValue(attr?.price ?? attr?.discountDetails?.[0]?.price);
    return {
      key: `equipment-plain-${index}`,
      name: attr?.name || '-',
      maxDurationText: formatDurationMonths(maxDurationMonths),
      controlCount: period,
      periodText: formatDerivedPeriod(maxDurationMonths, period),
      quantityWithUnit: formatQuantityWithUnit(numberValue(attr?.quantity), attr?.unit),
      unitPrice,
      totalPrice: numberValue(attr?.finalPrice),
    };
  });

  const equipmentGrouped: EquipmentGroupedRow[] = Object.keys(toolIdGrouped)
    .filter((key) => key !== 'noToolId')
    .map((toolKey) => {
      const toolData = toolIdGrouped[toolKey];
      const firstAttr = toolData?.attributes?.[0] || {};
      const period = normalizePeriod(firstAttr?.period);
      const maxDurationMonths = numberValue(firstAttr?.maxDurationMonths);
      return {
        key: `equipment-group-${toolKey}`,
        toolName: toolData?.toolName || '-',
        maxDurationText: formatDurationMonths(maxDurationMonths),
        controlCount: period,
        periodText: formatDerivedPeriod(maxDurationMonths, period),
        measurementDetails: (toolData?.attributes || [])
          .map((attr: any) => `${attr?.name} > ${formatQuantityWithUnit(numberValue(attr?.quantity), attr?.unit)}`)
          .join(' / '),
        totalPrice: numberValue(firstAttr?.groupTotalPrice),
      };
    });

  const allMeasurements: any[] = [];
  groupedByOffering.measurement.forEach((group: any) => {
    (group?.attributes || []).forEach((attr: any) => {
      allMeasurements.push({
        ...attr,
        isComposite: Boolean(group?.isComposite),
      });
    });
  });

  const measurementUngrouped: MeasurementRow[] = allMeasurements
    .filter((attr: any) => !attr?.isComposite)
    .map((attr: any, index: number) => {
      const period = normalizePeriod(attr?.period);
      const maxDurationMonths = numberValue(attr?.maxDurationMonths);
      return {
        key: `measurement-plain-${index}`,
        name: attr?.name || '-',
        maxDurationText: formatDurationMonths(maxDurationMonths),
        controlCount: period,
        periodText: formatDerivedPeriod(maxDurationMonths, period),
        quantityWithUnit: formatQuantityWithUnit(numberValue(attr?.quantity), attr?.unit),
        unitPrice: numberValue(attr?.price ?? attr?.discountDetails?.[0]?.price),
        totalPrice: numberValue(attr?.finalPrice),
      };
    });

  const measurementComposite: MeasurementCompositeRow[] = groupedByOffering.measurement
    .filter((group: any) => Boolean(group?.isComposite))
    .map((group: any, index: number) => {
      const firstAttr = group?.attributes?.[0] || {};
      const period = normalizePeriod(firstAttr?.period);
      const maxDurationMonths = numberValue(firstAttr?.maxDurationMonths);
      const detail = (group?.attributes || [])
        .map((attr: any) => `${attr?.name} > ${formatQuantityWithUnit(numberValue(attr?.quantity), attr?.unit)}`)
        .join(' / ');
      const totalPrice = numberValue(group?.finalPrice) || (group?.attributes || []).reduce((sum: number, attr: any) => {
        return sum + numberValue(attr?.finalPrice);
      }, 0);
      return {
        key: `measurement-composite-${index}`,
        name: group?.offeringName || firstAttr?.name || '-',
        maxDurationText: formatDurationMonths(maxDurationMonths),
        controlCount: period,
        periodText: formatDerivedPeriod(maxDurationMonths, period),
        detail,
        totalPrice,
      };
    });

  const equipmentServiceFeeDifferenceTotal = groupedByOffering.equipment.reduce((sum: number, item: any) => sum + numberValue(item?.serviceFeeDifference), 0);
  const electricServiceFeeDifferenceTotal = groupedByOffering.equipment.reduce((sum: number, item: any) => sum + numberValue(item?.electricServiceFeeDifference), 0);
  const machineryServiceFeeDifferenceTotal = groupedByOffering.equipment.reduce((sum: number, item: any) => sum + numberValue(item?.machineryServiceFeeDifference), 0);
  const equipmentOriginalTotal = groupedByOffering.equipment.reduce((sum: number, item: any) => sum + numberValue(item?.finalPrice), 0);
  const equipmentFinalTotal = calculateEquipmentDisplayTotal(groupedByOffering.equipment) + equipmentServiceFeeDifferenceTotal;

  const measurementServiceFeeDifferenceTotal = groupedByOffering.measurement.reduce((sum: number, item: any) => sum + numberValue(item?.serviceFeeDifference), 0);
  const measurementOriginalTotal = groupedByOffering.measurement.reduce((sum: number, item: any) => sum + numberValue(item?.finalPrice), 0);
  const measurementFinalTotal = groupedByOffering.measurement.reduce((sum: number, item: any) => sum + numberValue(item?.finalPrice), 0);

  const rawDisplayTotal = round2(equipmentFinalTotal + measurementFinalTotal);
  const subtotalExcludingKdv = resolveSubtotal(proposal, rawDisplayTotal);
  const discountNeededForBackendTotal = round2(rawDisplayTotal - subtotalExcludingKdv);
  const totalDiscountForDisplay = discountNeededForBackendTotal > 0
    ? discountNeededForBackendTotal
    : 0;
  let equipmentDiscountPart = 0;
  let measurementDiscountPart = 0;

  if (rawDisplayTotal > 0 && totalDiscountForDisplay > 0) {
    equipmentDiscountPart = round2((totalDiscountForDisplay * equipmentFinalTotal) / rawDisplayTotal);
    measurementDiscountPart = round2(totalDiscountForDisplay - equipmentDiscountPart);
  }

  let equipmentNetTotal = round2(equipmentFinalTotal - equipmentDiscountPart);
  let measurementNetTotal = round2(measurementFinalTotal - measurementDiscountPart);

  if (measurementFinalTotal > 0) {
    measurementNetTotal = round2(subtotalExcludingKdv - equipmentNetTotal);
  } else if (equipmentFinalTotal > 0) {
    equipmentNetTotal = subtotalExcludingKdv;
  }

  groupedByOffering.equipmentDiscountPart = equipmentDiscountPart;
  groupedByOffering.measurementDiscountPart = measurementDiscountPart;
  groupedByOffering.equipmentNet = equipmentNetTotal;
  groupedByOffering.measurementNet = measurementNetTotal;

  const equipmentSummaryRows: PriceSummaryRow[] = [];
  if (groupedByOffering.equipment.length > 0) {
    if (equipmentDiscountPart > 0 || equipmentServiceFeeDifferenceTotal > 0) {
      equipmentSummaryRows.push({
        label: 'Periyodik Kontrol Hizmeti Toplam',
        amount: round2(equipmentOriginalTotal - equipmentServiceFeeDifferenceTotal),
      });

      if (electricServiceFeeDifferenceTotal > 0) {
        equipmentSummaryRows.push({ label: 'Elektrik Servis Ücreti', amount: electricServiceFeeDifferenceTotal });
      }
      if (machineryServiceFeeDifferenceTotal > 0) {
        equipmentSummaryRows.push({ label: 'Makine Servis Ücreti', amount: machineryServiceFeeDifferenceTotal });
      }
      if (equipmentDiscountPart > 0) {
        equipmentSummaryRows.push({ label: 'İndirim (-)', amount: equipmentDiscountPart, isDiscount: true });
      }
    }
    equipmentSummaryRows.push({
      label: 'Periyodik Kontrol Hizmeti Genel Toplam',
      amount: equipmentNetTotal,
      isGrand: true,
    });
  }

  const measurementSummaryRows: PriceSummaryRow[] = [];
  if (groupedByOffering.measurement.length > 0) {
    if (measurementDiscountPart > 0 || measurementServiceFeeDifferenceTotal > 0) {
      measurementSummaryRows.push({
        label: 'İş Hijyeni Ölçüm Hizmeti Toplam',
        amount: round2(measurementOriginalTotal - measurementServiceFeeDifferenceTotal),
      });

      if (measurementDiscountPart > 0) {
        measurementSummaryRows.push({
          label: 'İndirim (-)',
          amount: measurementDiscountPart,
          isDiscount: true,
        });
      }

      if (measurementServiceFeeDifferenceTotal > 0) {
        measurementSummaryRows.push({
          label: 'Servis Ücreti',
          amount: measurementServiceFeeDifferenceTotal,
        });
      }
    }

    measurementSummaryRows.push({
      label: 'İş Hijyeni Ölçüm Hizmeti Genel Toplam',
      amount: measurementNetTotal,
      isGrand: true,
    });
  }

  const formulaRows: Array<[string, string]> = allEquipmentAttributes
    .filter((attr: any) => Boolean(attr?.toolId) && numberValue(attr?.quantity) !== 0)
    .map((attr: any) => {
      const quantity = numberValue(attr?.quantity);
      const period = normalizePeriod(attr?.period);
      const unitValue = quantity * period > 0 ? numberValue(attr?.finalPrice) / (quantity * period) : 0;
      const minPrice = Math.floor(unitValue / 10) * 10;
      return [`${quantity} ADETTEN SONRAKİ HER ${attr?.name || '-'} İÇİN`, `${formatToTRY(minPrice, false)} TL`];
    });

  const kdvRate = resolveKdvRate(proposal);
  const kdvAmount = round2(subtotalExcludingKdv * (kdvRate / 100));

  return {
    groupedByOffering,
    tables: {
      equipmentUngrouped,
      equipmentGrouped,
      measurementUngrouped,
      measurementComposite,
    },
    equipmentSummaryRows,
    measurementSummaryRows,
    formulaRows,
    subtotalExcludingKdv,
    kdvRate,
    kdvAmount,
    totalIncludingKdv: round2(subtotalExcludingKdv + kdvAmount),
  };
};
